import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonList,
  IonPage,
  IonSpinner,
  IonTitle,
  IonToolbar,
  useIonViewWillEnter,
} from '@ionic/react'
import React, { BaseSyntheticEvent, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { getVentilatorSelector } from '../selectors'
import { chevronBackOutline } from 'ionicons/icons'
import { setDateTime, getPreviousVentilatorState, saveVentilator } from '../store/ventilator.actions'
import { TabConstants } from '../../../../components/tabs/Tabs.constants'
import PCRHeader from '../../../../components/pcrHeader/PCRHeader.component'
import {
  setDeleteProcedure,
  setProcedureLoaderItem,
  syncPtaWithParentVitals,
} from '../../../timeline/store/timeline.actions'
import { TimelineConstants } from '../../../timeline/store/timeline.constants'
import ProcedureHeaderComponent from '../../../../components/procedure/ProcedureHeader.component'
import ProcedureFooterComponent from '../../../../components/procedure/ProcedureFooter.component'
import { getPatientTimelineSelector } from '../../../timeline/selectors/patientTimeline.selector'
import { compareObjectsIfEqual, filterOutObjectKeys } from '../../../../utilities/object.util'
import { initialVentilatorState } from '../store/ventilator.reducers'
import { showAlert } from '../../../../components/alert/store/alert.actions'
import VentilatorContent from './pageContent/ventilator.content'
import { goBack } from '../../../../utilities/navigation.util'
import { IServiceConfig } from '../../../configurations/store/configurations.types'
import { getServiceConfigSelector } from '../../../configurations/selectors/serviceConfig.selector'
import { IAlertState } from '../../../../components/alert/store/alert.types'
import { getCurrentDate, getCurrentTime } from '../../../../utilities'
import { VentilatorConstants } from '../store/ventilator.constants'

const Ventilator: React.FC = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const history = useHistory()
  const timelineState = useSelector(getPatientTimelineSelector)
  const { procedureLoader } = timelineState
  const ventilator = useSelector(getVentilatorSelector)

  const {
    date,
    time,
    action,
    assessedBy,
    procedureComplications,
    response,
    responsePNNV,
    authorization,
    comments,
    rolePerforming,
    rolePerformingPNNV,
    successfulAttempt,
    pta,
    attempts,
    isEkgImport,
    procedurePerformedByAnotherUnit,
    procedurePerformedByAnotherUnitPNNV,
  } = ventilator
  const serviceConfigData: IServiceConfig = useSelector(getServiceConfigSelector)
  const { DISPLAY_SECONDS } = serviceConfigData
  const displaySeconds = !!DISPLAY_SECONDS
  const isReferringFacilitySettings = action?.id === 3

  useEffect(() => {
    dispatch(getPreviousVentilatorState())
  }, [action])

  useEffect(() => {
    if (isReferringFacilitySettings && !pta) {
      dispatch({ type: VentilatorConstants.SET_PTA, pta: true })
      dispatch(syncPtaWithParentVitals(true))
    }
  }, [isReferringFacilitySettings, pta])

  const dateTimeValidationCheck = () => {
    if (!date || !time) {
      dispatch(showAlert({ header: '', message: 'Date / Time is required' }))
      return false
    }
    return true
  }

  const initialStateCheck = () => {
    if (pta && ((date && time) || (!date && !time))) return true
    const fieldsToFilter = ['key', 'addToTimeline', 'updateInputRows', 'airTemperatureUnit', 'airTemperatureBool']
    const currentState = filterOutObjectKeys(ventilator, fieldsToFilter)
    const initalState = filterOutObjectKeys(initialVentilatorState, fieldsToFilter)
    if (!compareObjectsIfEqual(currentState, initalState)) {
      return dateTimeValidationCheck()
    }
    return true
  }

  const onSaveClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (!pta && (!date || !time)) {
      dateTimeValidationCheck()
      return
    }
    const dateTimeValidationCheckResult = initialStateCheck()
    if (dateTimeValidationCheckResult) {
      history.push(TabConstants.TIMELINE_TAB)
    }
  }

  const onBackClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if ((!date || !time) && !pta) {
      const alert: IAlertState = {
        message: t('common.saveTimelineEntry'),
        buttons: [
          {
            text: t('common.buttons.Yes'),
            handler: () => {
              const date = getCurrentDate()
              let time = getCurrentTime()
              if (!displaySeconds) {
                time = time.replace(/(:\d+)(:\d+)$/, '$1:00')
              }

              dispatch({ type: 'ventilator/SET_TIME', time: time })
              dispatch({ type: 'ventilator/SET_DATE', date: date })
              dispatch(saveVentilator())
              goBack()
            },
          },
          {
            text: t('common.buttons.No'),
            role: 'destructive',
            handler: () => {
              const itemToRemove = {
                key: ventilator.key,
                rowActionType: TimelineConstants.SET_VENTILATOR_PROCEDURE_ITEM,
              }
              dispatch(setDeleteProcedure(true))
              dispatch({ type: TimelineConstants.DELETE_TIMELINE_ITEM, payload: itemToRemove })
              goBack()
            },
          },
        ],
      }
      dispatch(showAlert(alert))
      dispatch(setProcedureLoaderItem(false))
    } else {
      const dateTimeValidationCheck = initialStateCheck()
      if (dateTimeValidationCheck) {
        goBack()
      }
      dispatch(setProcedureLoaderItem(false))
    }
  }

  useIonViewWillEnter(() => {
    if (date || time) dispatch(setDateTime(time, date))
    dispatch(getPreviousVentilatorState())
    return
  })

  return (
    <IonPage id='VentilatorPage'>
      <PCRHeader />
      <IonHeader>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton data-testid='Back' fill='clear' onClick={onBackClick} color='primary-alt'>
              <IonIcon icon={chevronBackOutline} />
              {t('common.buttons.Back')}
            </IonButton>
          </IonButtons>
          <IonButtons slot='end'>
            <IonButton data-testid='Save' fill='clear' onClick={onSaveClick} color='primary-alt'>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
          <IonTitle>Ventilator</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent color='light'>
        {procedureLoader ? (
          <IonSpinner className='procedure-loader' data-testid='Spinner' />
        ) : (
          <IonList id='VentilatorProcedure'>
            <ProcedureHeaderComponent
              isPHI
              procedureType={'ventilator'}
              procedureDate={date}
              procedureTime={time}
              assessedBy={assessedBy}
              rolePerforming={rolePerforming}
              rolePerformingPNNV={rolePerformingPNNV}
              successfulAttempt={successfulAttempt}
              pta={pta}
              disablePta={isReferringFacilitySettings}
              attempts={attempts}
              isEkgImport={isEkgImport}
              procedurePerformedByAnotherUnit={procedurePerformedByAnotherUnit}
              procedurePerformedByAnotherUnitPNNV={procedurePerformedByAnotherUnitPNNV}
            />
            <VentilatorContent />
            <ProcedureFooterComponent
              isPHI
              procedureType={'ventilator'}
              procedureComplications={procedureComplications}
              response={response}
              responsePNNV={responsePNNV}
              authorization={authorization}
              comments={comments}
            />
          </IonList>
        )}
      </IonContent>
    </IonPage>
  )
}

export default Ventilator
